import { useState, useEffect, useCallback } from 'react'
import { getUserLocation } from '../utils/geolocation'

/**
 * Hook that resolves the user's GPS coordinates.
 * - Returns cached location from localStorage immediately
 * - Refreshes on mount, on app resume (visibilitychange), and on Capacitor appStateChange
 * Returns { latitude, longitude } or null.
 */
export function useUserLocation() {
  const [location, setLocation] = useState(null)

  const refresh = useCallback(() => {
    getUserLocation().then((loc) => {
      if (loc) setLocation(loc)
    })
  }, [])

  useEffect(() => {
    // Fetch on mount
    refresh()

    // Re-fetch when app comes back to foreground (covers force close + resume)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Capacitor app resume event
    let appListener = null
    if (window.Capacitor?.isNativePlatform?.()) {
      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) refresh()
        }).then((listener) => {
          appListener = listener
        })
      }).catch(() => {})
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (appListener) appListener.remove()
    }
  }, [refresh])

  return location
}
